import { useCallback, useState, useRef } from 'react';
import { decodeJwt } from 'jose';
import { AppConfig } from '../types/types';
import { useToast } from '../components/ToastContext';

const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;

export interface ConnectionDetails {
  token: string;
  participantName: string;
  roomName: string;
  serverUrl?: string;
}

export default function useConnectionDetails(appConfig: AppConfig) {
  const [connectionDetails, setConnectionDetails] = useState<ConnectionDetails | null>(null);
  const { showError } = useToast();

  const isFetchingRef = useRef(false);
  const toastShownRef = useRef(false); // ✅ Prevent duplicated toasts per fetch
  const lastFetchTimeRef = useRef<number>(0);
  const MIN_FETCH_INTERVAL = 3000;

  /** ---------- SAFE TOAST FUNCTION (no duplicates) ---------- */
  const safeShowError = (title: string, message: string) => {
    if (toastShownRef.current) return;
    toastShownRef.current = true;
    showError(title, message);
  };

  const fetchConnectionDetails = useCallback(async () => {
    const now = Date.now();
    if (isFetchingRef.current || now - lastFetchTimeRef.current < MIN_FETCH_INTERVAL) {
      return connectionDetails;
    }

    isFetchingRef.current = true;
    toastShownRef.current = false; // Reset duplicate blocker
    lastFetchTimeRef.current = now;

    const url = `${appConfig.backendUrl}/api/call/token`;

    try {
      /** ---------------- Build Payload ---------------- */
      const sessionInfo = JSON.parse(localStorage.getItem('chat_session_state') || 'null');
      const payload = {
        participantName: sessionInfo?.userName || 'Anonymous',
        session_id: sessionInfo?.sessionId || Date.now().toString(),
      };

      /** ---------------- API CALL ---------------- */
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Sandbox-Id': appConfig.sandboxId || ''
        },
        body: JSON.stringify(payload),
      });

      /** ---------------- Parse JSON Safely ---------------- */
      let data: any = null;
      try {
        data = await res.json();
      } catch {
        safeShowError('Invalid Response', 'Server returned an unreadable response.');
        throw new Error('Failed to parse server response');
      }

      /** ---------------- API-LEVEL ERRORS ---------------- */
      if (!res.ok || data.status === false || (data.status_code && data.status_code >= 400)) {
        const code = data.status_code || res.status;
        const message = data.message || data.detail || 'Unable to connect.';

        if (code === 404) {
          safeShowError('Service Unavailable', 'Voice service is temporarily offline.');
        } else {
          safeShowError('Connection Failed', message);
        }

        throw new Error(`API error ${code}: ${message}`);
      }

      /** ---------------- Missing required fields ---------------- */
      if (!data.token || !data.roomName || !data.participantName) {
        safeShowError('Invalid Response', 'Missing required server fields.');
        throw new Error('Missing required fields');
      }

      /** ---------------- SUCCESS ---------------- */
      setConnectionDetails(data);
      return data;
    } catch (err: any) {
      const msg = err?.message?.toLowerCase() || '';

      if (msg.includes('fetch') || msg.includes('network')) {
        safeShowError('Network Error', 'Please check your internet connection.');
      } else if (msg.includes('timeout')) {
        safeShowError('Timeout', 'The request took too long.');
      } else {
        safeShowError('Connection Failed', 'Unable to start the voice session.');
      }

      throw err;
    } finally {
      isFetchingRef.current = false;
    }
  }, [appConfig.backendUrl, appConfig.sandboxId, connectionDetails, showError]);

  /** ---------- Token Expiry Check ---------- */
  const isConnectionDetailsExpired = useCallback(() => {
    try {
      const token = connectionDetails?.token;
      if (!token) return true;

      const { exp } = decodeJwt(token);
      if (!exp) return true;

      return new Date(exp * 1000 - ONE_MINUTE_IN_MILLISECONDS) <= new Date();
    } catch {
      return true;
    }
  }, [connectionDetails]);

  /** ---------- Return existing OR fetch fresh ---------- */
  const existingOrRefreshConnectionDetails = useCallback(() => {
    if (!connectionDetails || isConnectionDetailsExpired()) {
      return fetchConnectionDetails();
    }
    return connectionDetails;
  }, [connectionDetails, fetchConnectionDetails, isConnectionDetailsExpired]);

  return {
    connectionDetails,
    refreshConnectionDetails: fetchConnectionDetails,
    existingOrRefreshConnectionDetails,
  };
}
